/**
 * Frontend Logger Utility
 * Security: Suppresses console output in production to prevent information leakage
 * 
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.error('Something failed:', error);
 *   logger.warn('Warning message');
 *   logger.log('Debug info');
 */

const isDev = import.meta.env.DEV;

/**
 * Logger that only outputs in development mode
 * In production, all console output is suppressed to prevent information leakage
 */
export const logger = {
    /**
     * Log error messages (suppressed in production)
     */
    error: (...args: unknown[]): void => {
        if (isDev) {
            console.error(...args);
        }
    },

    /**
     * Log warning messages (suppressed in production)
     */
    warn: (...args: unknown[]): void => {
        if (isDev) {
            console.warn(...args);
        }
    },

    /**
     * Log debug messages (suppressed in production)
     */
    log: (...args: unknown[]): void => {
        if (isDev) {
            console.log(...args);
        }
    },

    /**
     * Log debug messages (alias for log)
     */
    debug: (...args: unknown[]): void => {
        if (isDev) {
            console.log(...args);
        }
    },

    /**
     * Log info messages (suppressed in production)
     */
    info: (...args: unknown[]): void => {
        if (isDev) {
            console.info(...args);
        }
    },
};

export default logger;
